import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Typography
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  ArrowBackRounded,
  CloseRounded,
  PersonAddRounded,
  VisibilityRounded,
  VisibilityOffRounded
} from '@mui/icons-material'

import { useRegisterEmail } from '../hooks/auth'

export default function Register() {
  const [registerEmail, loadingRegister, errorRegister, clearErrorRegister] = useRegisterEmail()
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showPasswordButton, setShowPasswordButton] = useState(false)
  const navigate = useNavigate()

  const handleShowPassword = () => setShowPassword(!showPassword)

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()

  const handleRegisterAccount = async () => {
    setShowPassword(false)
    registerEmail(email, password)
    navigate('/login')
  }

  return (
    <Container maxWidth="xs">
      <Box ml={-1} mt={1}>
        <IconButton
          component={RouterLink}
          to="/login"
          aria-label="delete"
          size="large"
        >
          <ArrowBackRounded />
        </IconButton>
      </Box>

      <Box
        mt={1}
        sx={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <PersonAddRounded fontSize="large" color="disabled" />
        <Typography variant="h6" component="h1" ml={2}>
          Register account
        </Typography>
      </Box>

      <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
        <InputLabel htmlFor="email" error={!!errorRegister}>Email</InputLabel>
        <OutlinedInput
          id="email"
          label="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={loadingRegister}
          error={!!errorRegister}
        />
      </FormControl>

      <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
        <InputLabel htmlFor="password">Password</InputLabel>
        <OutlinedInput
          id="password"
          label="Password"
          value={password}
          type={showPassword ? 'text' : 'password'}
          onFocus={() => setShowPasswordButton(true)}
          onBlur={() => setShowPasswordButton(false)}
          onChange={e => setPassword(e.target.value)}
          onKeyUp={e => e.key === 'Enter' && handleRegisterAccount()}
          disabled={loadingRegister}
          endAdornment={showPasswordButton &&
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleShowPassword}
                onMouseDown={handleMouseDown}
                disabled={loadingRegister}
                edge="end"
              >
                {showPassword ? <VisibilityOffRounded /> : <VisibilityRounded />}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>

      <Box mt={2}>
        <LoadingButton
          fullWidth
          size="large"
          loading={loadingRegister}
          variant="contained"
          disabled={loadingRegister}
          onClick={handleRegisterAccount}
        >
          Register
        </LoadingButton>
      </Box>

      <Snackbar
        open={!!errorRegister}
        autoHideDuration={6000}
        onClose={clearErrorRegister}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={clearErrorRegister}
            >
              <CloseRounded />
            </IconButton>
          }
        >
          {errorRegister?.email}
          {errorRegister?.password}
          {errorRegister?.other}
        </Alert>
      </Snackbar>
    </Container>
  )
}
